import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Button, Input } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';

export function WardPhoneEditor({ ward }: { ward: { id: string; name: string; nurseStationPhone?: string | null } }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(ward.nurseStationPhone ?? '');

  const save = useMutation({
    mutationFn: () => put(`/ipd/wards/${ward.id}`, { nurseStationPhone: phone.trim() || null }),
    onSuccess: () => {
      toast.success(tr('ipd', 'x_nurse_station_phone_saved', { name: ward.name }), tr('ipd', 'critical_results_for_patients_on_this_ward_are'));
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['wards'] });
    },
    onError: (caught) => toast.error(tr('ipd', 'could_not_save_the_phone'), describeError(caught)),
  });

  if (!editing) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
        <Phone className="me-1 h-4 w-4" /> {ward.nurseStationPhone ? tr('ipd', 'nurse_station_x', { nurseStationPhone: ward.nurseStationPhone }) : tr('ipd', 'add_nurse_station_phone')}
      </Button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <PhoneText aria-label={tr('ipd', 'nurse_station_phone')} className="w-40" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="042 1234567" />
      <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
        {tr('ipd', 'save')}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
        {tr('ipd', 'cancel')}
      </Button>
    </span>
  );
}
